import { useApi } from '@shared/api'
import type {
  IndividualNotifyPayload,
  GenderNotifyPayload,
  AbandonedCartNotifyPayload,
  NewGuestsNotifyPayload,
  BroadcastNotifyPayload,
  NotificationSendResponse,
  NotificationListItem,
  AdminNotificationStats,
  NotificationFilters,
  Notification,
} from '@entities/notification'

// TODO: вынести в fsd-pages/admin/model/types/notifications.ts
export interface MarkNotificationPayload {
  /** UUID персонального уведомления — приходит в data индивидуального пуша. */
  notificationId?: string
  /**
   * Идентификатор массовой рассылки. Персонального UUID у неё нет: мультикаст уходит
   * одним сообщением на все токены, поэтому получателя сервер находит по паре
   * «рассылка + пользователь» из токена.
   */
  broadcastId?: string
}

export interface NotificationAction {
  code: string
  name: string
  description: string
  requiredDataFields: string[]
  exampleData: Record<string, any>
  isActive: boolean
}

export interface NotificationScreen {
  code: string
  name: string
  description: string
  platform: 'all' | 'web' | 'ios' | 'android'
  requiredParams: string[]
  isActive: boolean
}

export interface NotificationActionsResponse {
  success: boolean
  data: NotificationAction[]
  count: number
}

export interface NotificationScreensResponse {
  success: boolean
  data: NotificationScreen[]
  count: number
}

export interface NotificationConfigResponse {
  success: boolean
  data: { actions: NotificationAction[]; screens: NotificationScreen[] }
}

class APIAdminNotification {
  /**
   * Отправка уведомлений.
   *
   * ⚠️ Все методы бьют в NestJS (`/api/v2/admin/notifications/*`). Раньше здесь
   * стояли адреса легаси-роутов Express (`/admin/notify`, `/admin/gender`,
   * `/admin/abandoned-cart`, `/admin/new-guests`) — на v2 их нет, и каждая
   * кнопка отправки в админке отвечала 404.
   */
  async notifyUser(payload: IndividualNotifyPayload): Promise<NotificationSendResponse> {
    const { data } = await useApi(true, true).post<NotificationSendResponse>('/admin/notifications/individual', {
      body: payload,
    })
    return data
  }

  async notifyByGender(payload: GenderNotifyPayload): Promise<NotificationSendResponse> {
    const { gender, ...rest } = payload
    const { data } = await useApi(true, true).post<NotificationSendResponse>('/admin/notifications/broadcast', {
      body: { ...rest, segment: { sex: gender } },
    })
    return data
  }

  /** Рассылка всем гостям заведения: пустой сегмент. */
  async notifyAll(payload: BroadcastNotifyPayload): Promise<NotificationSendResponse> {
    const { data } = await useApi(true, true).post<NotificationSendResponse>('/admin/notifications/broadcast', {
      body: payload,
    })
    return data
  }

  async notifyAbandonedCart(payload: AbandonedCartNotifyPayload): Promise<NotificationSendResponse> {
    const { data } = await useApi(true, true).post<NotificationSendResponse>('/admin/notifications/abandoned-cart', {
      body: payload,
    })
    return data
  }

  async notifyNewGuests(payload: NewGuestsNotifyPayload): Promise<NotificationSendResponse> {
    const { data } = await useApi(true, true).post<NotificationSendResponse>('/admin/notifications/new-guests', {
      body: payload,
    })
    return data
  }

  async getNotifications(filters?: NotificationFilters): Promise<{ notifications: NotificationListItem[]; total: number }> {
    const params = new URLSearchParams()
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined) params.append(key, String(value))
      }
    }
    const { data } = await useApi(true, true).get<{ notifications: NotificationListItem[]; total: number }>(
      `/admin/notifications?${params.toString()}`,
    )
    return data
  }

  async getNotificationStats(): Promise<AdminNotificationStats> {
    const { data } = await useApi(true, true).get<AdminNotificationStats>('/admin/notifications/stats')
    return data
  }

  async getNotificationDetails(id: number): Promise<Notification> {
    const { data } = await useApi(true, true).get<Notification>(`/admin/notifications/${id}`)
    return data
  }

  async deleteNotification(id: number): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).delete<{ success: boolean }>(`/admin/notifications/${id}`)
    return data
  }

  async retryNotification(id: number): Promise<NotificationSendResponse> {
    const { data } = await useApi(true, true).post<NotificationSendResponse>(`/admin/notifications/${id}/retry`)
    return data
  }

  async getAudienceStats(): Promise<{
    total_users: number
    male_users: number
    female_users: number
    users_with_abandoned_carts: number
    new_guests: number
  }> {
    const { data } = await useApi(true, true).get('/admin/notifications/audience-stats')
    return data
  }

  async markDelivered(payload: MarkNotificationPayload): Promise<void> {
    await useApi(true, true).post('/notifications/mark_delivered', { body: payload })
  }

  async markOpened(payload: MarkNotificationPayload): Promise<void> {
    await useApi(true, true).post('/notifications/mark_opened', { body: payload })
  }

  async getNotificationActions(includeInactive = false): Promise<NotificationActionsResponse> {
    const url = includeInactive
      ? '/notifications/config/actions?includeInactive=true'
      : '/notifications/config/actions'
    const { data } = await useApi(true, true).get<NotificationActionsResponse>(url)
    return data
  }

  async getNotificationScreens(platform = 'all', includeInactive = false): Promise<NotificationScreensResponse> {
    const params = new URLSearchParams()
    if (platform !== 'all') params.append('platform', platform)
    if (includeInactive) params.append('includeInactive', 'true')
    const qs = params.toString()
    const { data } = await useApi(true, true).get<NotificationScreensResponse>(
      qs ? `/notifications/config/screens?${qs}` : '/notifications/config/screens',
    )
    return data
  }

  async getAllNotificationConfig(platform = 'all', includeInactive = false): Promise<NotificationConfigResponse> {
    const params = new URLSearchParams()
    if (platform !== 'all') params.append('platform', platform)
    if (includeInactive) params.append('includeInactive', 'true')
    const qs = params.toString()
    const { data } = await useApi(true, true).get<NotificationConfigResponse>(
      qs ? `/notifications/config/all?${qs}` : '/notifications/config/all',
    )
    return data
  }

  async uploadNotificationImage(file: File): Promise<{
    key: string
    url: string
    filename: string
    mimetype: string
    size: number
  }> {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await useApi(true, true).post<{
      key: string
      url: string
      filename: string
      mimetype: string
      size: number
    }>('/admin/notifications/upload-image', { body: formData })
    return data
  }
}

export const apiAdminNotification = new APIAdminNotification()
export default apiAdminNotification
